import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaStar, FaPaperPlane, FaSpinner, FaQuoteLeft, FaClock, FaCheckCircle } from 'react-icons/fa';
import { SEO, PageSection, Breadcrumb } from '../../components/common';
import { fetchApprovedReviews as getApprovedReviews, submitReview } from '../../services/reviewService';
import { formatDate } from '../../utils/formatDate';
import { notifyError, notifyPending, NotificationMessages } from '../../utils/notifications';

const Reviews = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'reviews'; // Default to 'reviews' tab
  
  const [formData, setFormData] = useState({
    name: '',
    rating: 0,
    comment: ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [activeTab, setActiveTab] = useState(tabFromUrl); // Set from URL parameter
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    loadApprovedReviews();
  }, []);

  const loadApprovedReviews = async () => {
    setIsLoadingReviews(true);
    try {
      const reviews = await getApprovedReviews();
      setApprovedReviews(reviews);
    } catch (error) {
      console.error('Error fetching approved reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      notifyError(NotificationMessages.VALIDATION_NAME_REQUIRED);
      return;
    }
    if (formData.name.trim().length < 2) {
      notifyError(NotificationMessages.VALIDATION_NAME_TOO_SHORT);
      return;
    }
    if (formData.name.trim().length > 50) {
      notifyError(NotificationMessages.VALIDATION_NAME_TOO_LONG);
      return;
    }
    if (formData.rating === 0) {
      notifyError(NotificationMessages.VALIDATION_RATING_REQUIRED);
      return;
    }
    if (!formData.comment.trim()) {
      notifyError(NotificationMessages.VALIDATION_COMMENT_REQUIRED);
      return;
    }
    if (formData.comment.length < 10) {
      notifyError(NotificationMessages.VALIDATION_COMMENT_TOO_SHORT);
      return;
    }

    setIsSubmitting(true);

    try {
      await submitReview(formData);

      // Show pending notification with appropriate icon and color
        notifyPending(NotificationMessages.REVIEW_SUBMITTED, { duration: 6000 });
        
        // Show success message on the form
        setShowSuccessMessage(true);
        
        // Reset form
        setFormData({
          name: '',
          rating: 0,
          comment: ''
        });
        
        // Hide success message after 8 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 8000);
        
        // Stay on the same page - DO NOT switch tabs
    } catch (error) {
      console.error('Error submitting review:', error);
      notifyError(error.message || NotificationMessages.REVIEW_SUBMIT_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAverageRating = () => {
    if (approvedReviews.length === 0) return 0;
    const sum = approvedReviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / approvedReviews.length).toFixed(1);
  };

  return (
    <>
      <SEO
        title="Client Reviews | Arvix Creative"
        description="See what our clients say about working with Arvix Creative. Read reviews and share your experience."
        keywords="reviews, testimonials, feedback, client experience, arvix creative"
      />

      <PageSection className="min-h-screen py-12 sm:py-16 md:py-20">
        <div>
          <Breadcrumb items={[{ label: "Reviews" }]} className="mb-8" />
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-3 sm:mb-4">
              Client Reviews
            </h1>
            <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6 px-2">
              See what our clients say about working with us
            </p>
            
            {/* Average Rating Display */}
            {approvedReviews.length > 0 && (
              <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 bg-yellow-50 border border-yellow-200 rounded-lg px-4 sm:px-6 py-3">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={`text-xl sm:text-2xl ${
                        i < Math.round(getAverageRating()) ? 'text-yellow-400' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xl sm:text-2xl font-bold text-designer-dark">{getAverageRating()}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{approvedReviews.length} {approvedReviews.length === 1 ? 'Review' : 'Reviews'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                activeTab === 'reviews'
                  ? 'bg-designer-primary text-white shadow-red'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              View Reviews ({approvedReviews.length})
            </button>
            <button
              onClick={() => setActiveTab('submit')}
              className={`px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                activeTab === 'submit'
                  ? 'bg-designer-primary text-white shadow-red'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Write a Review
            </button>
          </div>

          {/* Approved Reviews Section */}
          {activeTab === 'reviews' && (
            <div>
              {/* Verification Notice */}
              <div className="mb-6 sm:mb-8 max-w-4xl mx-auto">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 sm:p-6 shadow-lg">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                        <FaCheckCircle className="text-white text-lg sm:text-xl" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-green-900 mb-2 flex flex-wrap items-center gap-2">
                        All Reviews Are Verified
                      </h3>
                      <p className="text-sm sm:text-base text-green-800 mb-3">
                        Every review you see here has been carefully verified and approved by our team 
                        to ensure authenticity and quality.
                      </p>
                      <div className="bg-white/70 rounded-lg p-3 border border-green-200">
                        <p className="text-xs sm:text-sm text-green-900">
                          <strong>📝 Submitted a review?</strong> Please wait for verification and approval. 
                          Your feedback is important to us and will be reviewed within 24-48 hours before being published.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoadingReviews ? (
                <div className="text-center py-12">
                  <FaSpinner className="animate-spin text-4xl text-designer-primary mx-auto mb-4" />
                  <p className="text-gray-600">Loading reviews...</p>
                </div>
              ) : approvedReviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-gray-200 shadow-red">
                  <p className="text-gray-500 text-lg mb-4">No reviews yet</p>
                  <button
                    onClick={() => setActiveTab('submit')}
                    className="bg-designer-primary text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-all"
                  >
                    Be the first to review
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {approvedReviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-white rounded-2xl p-4 sm:p-6 shadow-red border border-gray-200 hover:shadow-red-hover transition-all overflow-hidden"
                    >
                      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-designer-primary to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl">
                            {review.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-semibold text-designer-dark mb-1 break-all">
                            {review.name}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <div className="flex flex-shrink-0">
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  className={`text-xs sm:text-sm ${
                                    i < review.rating ? 'text-yellow-400' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs sm:text-sm text-gray-500">
                              {formatDate(review.created_at, 'long')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <FaQuoteLeft className="absolute -top-2 -left-2 text-xl sm:text-2xl text-gray-200" />
                        <p className="text-sm sm:text-base text-gray-600 pl-5 sm:pl-6 relative z-10 break-words">{review.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Review Form */}
          {activeTab === 'submit' && (
            <div className="max-w-3xl mx-auto">
              {/* Success Message Banner */}
              {showSuccessMessage && (
                <div className="mb-4 sm:mb-6 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-2xl p-4 sm:p-6 shadow-lg animate-fade-in">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                        <FaClock className="text-white text-lg sm:text-xl animate-pulse" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-orange-900 mb-2 flex flex-wrap items-center gap-2">
                        <FaCheckCircle className="text-orange-600" />
                        Review Submitted Successfully!
                      </h3>
                      <p className="text-sm sm:text-base text-orange-800 font-medium mb-3">
                        Thank you for taking the time to share your feedback!
                      </p>
                      <div className="bg-white/80 rounded-lg p-3 sm:p-4 border border-orange-200">
                        <p className="text-xs sm:text-sm text-orange-900">
                          <strong>⏱️ Pending Approval:</strong> Your review is currently being reviewed by our team. 
                          Once approved, it will be visible to everyone on our reviews page. 
                          We typically review submissions within 24-48 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-red border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-designer-dark font-medium mb-2 text-sm sm:text-base">
                  Your Name <span className="text-designer-primary">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  maxLength={50}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base bg-gray-50 border border-gray-300 rounded-lg text-designer-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-designer-primary focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
                <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                  {formData.name.length}/50 characters
                </div>
              </div>

              {/* Rating Field */}
              <div>
                <label className="block text-designer-dark font-medium mb-2 text-sm sm:text-base">
                  Your Rating <span className="text-designer-primary">*</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="transition-all transform hover:scale-110 focus:outline-none"
                      disabled={isSubmitting}
                    >
                      <FaStar
                        className={`text-3xl sm:text-4xl transition-colors ${
                          star <= (hoveredRating || formData.rating)
                            ? 'text-yellow-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  {formData.rating > 0 && (
                    <span className="ml-1 sm:ml-3 text-designer-dark font-medium text-sm sm:text-base">
                      {formData.rating} {formData.rating === 1 ? 'star' : 'stars'}
                    </span>
                  )}
                </div>
              </div>

              {/* Comment Field */}
              <div>
                <label htmlFor="comment" className="block text-designer-dark font-medium mb-2 text-sm sm:text-base">
                  Your Review <span className="text-designer-primary">*</span>
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  placeholder="Share your experience with us..."
                  rows={5}
                  maxLength={1000}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base bg-gray-50 border border-gray-300 rounded-lg text-designer-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-designer-primary focus:border-transparent transition-all resize-none"
                  disabled={isSubmitting}
                />
                <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                  {formData.comment.length}/1000 characters
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-designer-primary to-red-600 hover:from-red-600 hover:to-designer-primary text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 text-sm sm:text-base rounded-lg transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-red hover:shadow-red-hover flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="text-sm sm:text-base" />
                    Submit Review
                  </>
                )}
              </button>

              {/* Info Text */}
              <p className="text-gray-500 text-xs sm:text-sm text-center">
                Your review will be published after approval by our team.
              </p>
            </form>
          </div>

            {/* Additional Info */}
            <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-block bg-red-50 border border-red-100 rounded-lg px-4 sm:px-6 py-3 sm:py-4">
                <p className="text-gray-600 text-xs sm:text-sm">
                  💡 <strong>Tip:</strong> Honest feedback helps us serve you better and assists others in making informed decisions.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </PageSection>
    </>
  );
};

export default Reviews;
